import React from 'react';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { useTheme } from '@/ui/theme/ThemeContext';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  style?: React.CSSProperties;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'default',
  size = 'md',
  className = '',
  style = {},
  children,
  disabled,
  loading = false,
  leftIcon,
  rightIcon,
  ...props 
}) => {
  const { t } = useSettings();
  const { cn } = useSettings();
  const { theme, isDarkMode } = useTheme();
  
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2 dark:focus-visible:ring-gray-600 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed select-none';
  
  const variants = {
    default: cn(theme.button, isDarkMode ? 'shadow-sm hover:shadow-md' : ''),
    secondary: cn(
      theme.bgSecondary, 
      theme.textPrimary, 
      theme.hover, 
      'border', 
      theme.borderPrimary,
      isDarkMode ? 'shadow-sm hover:shadow-md' : ''
    ),
    outline: cn(
      theme.bgSecondary,
      theme.textPrimary,
      'border',
      theme.borderPrimary,
      theme.hover,
      isDarkMode ? 'shadow-sm hover:shadow-md' : ''
    ),
    ghost: cn(
      theme.textSecondary,
      theme.hover,
      'bg-transparent'
    ),
    destructive: cn(theme.accentRed, isDarkMode ? 'shadow-sm hover:shadow-md' : '')
  };
  
  const sizes = {
    xs: 'h-6 px-2 text-xs rounded gap-1 min-w-[24px]',
    sm: 'h-8 px-3 text-sm rounded-md gap-1.5 min-w-[32px]',
    md: 'h-10 px-4 py-2 text-sm rounded-md gap-2 min-w-[40px]',
    lg: 'h-11 px-6 text-base rounded-md gap-2 min-w-[44px]',
    xl: 'h-12 px-8 text-lg rounded-lg gap-3 min-w-[48px]'
  };
  
  
  return (
    <button
      className={cn(
        baseClasses,
        variants[variant],
        sizes[size],
        loading && 'cursor-wait opacity-75',
        className
      )}
      style={style}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          {t('Loading...')}
        </>
      ) : (
        <>
          {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="inline-flex items-center">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};
